import React, { useId, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { CheckChip, SelectFilter } from './controls';
import {
  MODE_CARDS,
  MODE_FOLD,
  MODE_TABLE,
  TRACK_CARD_BELOW,
  card_layout,
  card_sort_options,
  card_sort_value,
  col_tracks,
  column_placement,
  dropped_values,
  fold_order,
  has_extra_sorts,
  is_tracked,
  mode_name,
  parse_card_sort,
  present_parts,
  track_card_layout
} from './table_layout';
import { useFitLayout, useOverflowReport } from './use_container_layout';
import './responsive_table.css';

/*
 * One tree only: the full table, the folded table or cards, whichever is richest and fits the
 * container without sideways scroll (table_layout.fit_level). Selection, sort and page live in the
 * caller, so switching layouts (resize, rotate, sidebar) keeps them. Column fields: role (select |
 * identifier | amount | status | date | actions), min_width, fold: { into, below?, block },
 * in_table / in_card (twin columns only), card_render (the card's rendering of the cell).
 * List-row pattern (§2.3): track ('34px' | { compact, full }) renders a colgroup and a fixed layout
 * that never folds; line: { from, bare } renders a note line as-is; sorts: [{ sort_key, label }] are
 * sorts of values composed into a column, offered in a sort select.
 */
const INTERACTIVE = 'button, a, input, select, textarea, label, summary';
const ARROWS = { ascending: '▲', descending: '▼', none: '' };
// Status and actions cells wrap (resolution text, an action's reason); their pills, buttons and
// links are nowrap on their own (.tp-pill, .tp-rt-actions rules).
const NOWRAP_ROLES = ['amount', 'date', 'select'];

const aria_sort = (sort, key) => {
  if (!sort || sort.key !== key) return 'none';
  return sort.dir === 'asc' ? 'ascending' : 'descending';
};

const cell = (c, row) => (c.render ? c.render(row) : row[c.key]);
// Card lines 1-2 drop the column headers; a screen reader still hears "Label: value".
const SrLabel = ({ c }) =>
  typeof c.label === 'string' && c.label ? <span className="sr-only">{c.label}: </span> : null;
const card_cell = (c, row) => (c.card_render ? c.card_render(row) : cell(c, row));

const col_class = (c) =>
  [
    c.align ? `tp-col-align-${c.align}` : '',
    NOWRAP_ROLES.includes(c.role) ? 'tp-rt-nowrap' : '',
    c.role === 'actions' ? 'tp-rt-actions' : '',
    c.role === 'select' ? 'tp-rt-select' : '',
    c.role === 'status' ? 'tp-rt-status' : ''
  ]
    .filter(Boolean)
    .join(' ') || undefined;

const col_style = (c) => {
  const style = {};
  if (c.align) style.textAlign = c.align;
  if (c.min_width) style.minWidth = c.min_width;
  return Object.keys(style).length ? style : undefined;
};

const Header = ({ c, sort, on_sort }) => {
  if (!on_sort || !c.sort_key) {
    return (
      <th scope="col" className={col_class(c)} style={col_style(c)}>
        {c.label}
      </th>
    );
  }
  const state = aria_sort(sort, c.sort_key);
  return (
    <th
      scope="col"
      className={col_class(c)}
      style={col_style(c)}
      aria-sort={state === 'none' ? undefined : state}
    >
      <button type="button" className="tp-th-sort" onClick={() => on_sort(c.sort_key)}>
        {c.label}
        <span className="tp-th-sort-arrow" aria-hidden="true">
          {ARROWS[state]}
        </span>
      </button>
    </th>
  );
};

// A folded column keeps its value and its label, in a grid inside the cell it folds into (numbers
// right, lined up row to row), or (fold.block: 'end') a right-aligned block for money and status.
// The target column's fold_summary(row) may stand in for the list (e.g. every amount needs review).
const Folded = ({ columns, row, into }) => {
  const list = columns || [];
  if (!list.length) return null;
  const summary = into && into.fold_summary ? into.fold_summary(row) : null;
  if (summary) return <div className="tp-rt-fold-summary">{summary}</div>;
  const lines = list.filter((f) => !(f.fold && f.fold.block === 'end'));
  const end = list.filter((f) => f.fold && f.fold.block === 'end');
  const render_one = (f) => {
    const value = cell(f, row);
    if (value === null || value === undefined || value === '') return null;
    return (
      <div
        key={f.key}
        className="tp-rt-fold"
        data-role={f.role || undefined}
        data-num={f.align === 'right' || f.role === 'amount' ? '' : undefined}
      >
        {typeof f.label === 'string' && f.label ? (
          <span className="tp-rt-fold-label">{f.label}</span>
        ) : null}
        <span className="tp-rt-fold-value">{value}</span>
      </div>
    );
  };
  return (
    <>
      {lines.length ? <div className="tp-rt-fold-list">{lines.map(render_one)}</div> : null}
      {end.length ? <div className="tp-rt-fold-end">{end.map(render_one)}</div> : null}
    </>
  );
};

// The `line` columns with a value in this row.
const line_parts = (lines, row) =>
  lines
    .map((c) => ({ c, v: cell(c, row) }))
    .filter(({ v }) => v !== null && v !== undefined && v !== '');

// A `line` column's labelled value under the row, spanning from its `from` column to the end.
const SecondLine = ({ parts, inline }) => {
  const from = Math.max(
    0,
    inline.findIndex((c) => c.key === parts[0].c.line.from)
  );
  return (
    <>
      {from ? <td colSpan={from} className="tp-rt-line-pad" /> : null}
      <td colSpan={inline.length - from} className="tp-rt-line">
        {parts.every(({ c }) => c.line.bare) ? (
          parts.map(({ c, v }) => <React.Fragment key={c.key}>{v}</React.Fragment>)
        ) : (
          <div className="tp-rt-line-list">
            {parts.map(({ c, v }) => (
              <div key={c.key} className="tp-rt-fold">
                {typeof c.label === 'string' && c.label ? (
                  <span className="tp-rt-fold-label">{c.label}</span>
                ) : null}
                <span className="tp-rt-fold-value">{v}</span>
              </div>
            ))}
          </div>
        )}
      </td>
    </>
  );
};

const TableView = ({
  columns,
  rows,
  rowKey,
  sort,
  on_sort,
  rowClassName,
  onRowClick,
  width,
  layout,
  on_overflow
}) => {
  const { inline, folded, lines } = column_placement(columns, width, layout);
  const tracks = col_tracks(inline);
  // A table wider than its container folds one more column instead of scrolling sideways.
  const ref = useOverflowReport(width, (need) => on_overflow(layout, need));
  const click = (row) =>
    onRowClick
      ? (e) => {
          if (e.target.closest && e.target.closest(INTERACTIVE)) return;
          onRowClick(row);
        }
      : undefined;
  return (
    <div ref={ref} className="tp-rt-fit">
      <table className="tp-table tp-rt-table" data-tracks={tracks ? '' : undefined}>
        {tracks ? (
          <colgroup>
            {/* Widths come from CSS (a container query picks compact or full). */}
            {tracks.map((t) => (
              <col
                key={t.key}
                className="tp-rt-col"
                data-col={t.key}
                style={
                  t.compact ? { '--tp-track': t.compact, '--tp-track-full': t.full } : undefined
                }
              />
            ))}
          </colgroup>
        ) : null}
        <thead>
          <tr>
            {inline.map((c) => (
              <Header key={c.key} c={c} sort={sort} on_sort={on_sort} />
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const extra = [
              (rowClassName && rowClassName(row)) || '',
              onRowClick ? 'tp-row-click' : ''
            ]
              .filter(Boolean)
              .join(' ');
            const parts = line_parts(lines, row);
            const has_line = parts.length > 0;
            return (
              <React.Fragment key={rowKey(row)}>
                {/* Row click is a mouse convenience; the keyboard path is the caller's button in the row. */}
                <tr
                  data-row-key={rowKey(row)}
                  className={
                    [extra, has_line ? 'tp-rt-has-line' : ''].filter(Boolean).join(' ') || undefined
                  }
                  onClick={click(row)}
                >
                  {inline.map((c) => (
                    <td
                      key={c.key}
                      className={col_class(c)}
                      style={col_style(c)}
                      data-role={c.role || undefined}
                    >
                      {/* In the actions cell the folded money/status sits above the buttons. */}
                      {c.role === 'actions' ? (
                        <Folded columns={folded[c.key]} row={row} into={c} />
                      ) : null}
                      {c.role === 'select' ? (
                        // The label widens the checkbox's hit area to the whole cell.
                        // eslint-disable-next-line jsx-a11y/label-has-associated-control
                        <label className="tp-rt-check">{cell(c, row)}</label>
                      ) : (
                        cell(c, row)
                      )}
                      {c.role !== 'actions' ? (
                        <Folded columns={folded[c.key]} row={row} into={c} />
                      ) : null}
                    </td>
                  ))}
                </tr>
                {has_line ? (
                  <tr
                    data-line-for={rowKey(row)}
                    className={['tp-rt-line-row', extra].filter(Boolean).join(' ')}
                    onClick={click(row)}
                  >
                    <SecondLine parts={parts} inline={inline} />
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// `action`: a card with no body and one action shows it at the end of line 2 (no empty band).
const Line2 = ({ columns, row, action }) => {
  const parts = present_parts(
    columns.map((c) => {
      const v = card_cell(c, row);
      return v === null || v === undefined || v === '' ? null : { c, key: c.key, v };
    })
  );
  if (!parts.length && !action) return null;
  return (
    <div className="tp-rcard-l2">
      {parts.map((p, i) => (
        <React.Fragment key={p.key}>
          {i > 0 ? (
            <span className="tp-rcard-sep" aria-hidden="true">
              ·
            </span>
          ) : null}
          <span>
            <SrLabel c={p.c} />
            {p.v}
          </span>
        </React.Fragment>
      ))}
      {action ? <span className="tp-rcard-l2-action">{action}</span> : null}
    </div>
  );
};

const Card = ({ layout, row, rowKey, cardTitle, rowClassName, onRowClick }) => {
  const extra = (rowClassName && rowClassName(row)) || '';
  const footer = layout.actions.map((c) => card_cell(c, row)).filter((v) => v);
  const fields = layout.body
    .map((c) => ({ c, v: card_cell(c, row) }))
    .filter(({ v }) => v !== null && v !== undefined && v !== '');
  const inline_action = !fields.length && footer.length === 1;
  // ...on line 1 when there is no line 2 either (one-row card: identifier left, action right).
  const line1_action =
    inline_action && !present_parts(layout.line2.map((c) => card_cell(c, row) || null)).length;
  return (
    // A mouse convenience only: the card's buttons are the keyboard path.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      data-row-key={rowKey(row)}
      className={`tp-rcard${extra ? ` ${extra}` : ''}${onRowClick ? ' tp-row-click' : ''}`}
      onClick={
        onRowClick
          ? (e) => {
              if (e.target.closest && e.target.closest(INTERACTIVE)) return;
              onRowClick(row);
            }
          : undefined
      }
    >
      <div className="tp-rcard-l1">
        {layout.select.map((c) => (
          // The label widens the checkbox's hit area to the card corner.
          // eslint-disable-next-line jsx-a11y/label-has-associated-control
          <label key={c.key} className="tp-rcard-check">
            {card_cell(c, row)}
          </label>
        ))}
        <div className="tp-rcard-id">
          {layout.identifier ? <SrLabel c={layout.identifier} /> : null}
          {layout.identifier
            ? card_cell(layout.identifier, row)
            : cardTitle
              ? cardTitle(row)
              : null}
        </div>
        {layout.amount ? (
          <div className="tp-rcard-amt tp-rt-nowrap">
            <SrLabel c={layout.amount} />
            {card_cell(layout.amount, row)}
          </div>
        ) : null}
        {line1_action ? <span className="tp-rcard-l2-action">{footer[0]}</span> : null}
      </div>
      <Line2
        columns={layout.line2}
        row={row}
        action={inline_action && !line1_action ? footer[0] : null}
      />
      {fields.length ? (
        <dl className="tp-card-item-grid tp-rcard-body">
          {fields.map(({ c, v }) => (
            <div
              key={c.key}
              className={`tp-card-field${c.card_wide || c.line ? ' tp-card-field-wide' : ''}`}
            >
              {c.label ? <dt className="tp-card-field-label">{c.label}</dt> : null}
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {footer.length && !inline_action ? <div className="tp-rcard-ft">{footer}</div> : null}
    </div>
  );
};

// A track list's card: checkbox · identifier · status, then each block full width, notes, actions.
const TrackCard = ({ layout, row, rowKey, rowClassName, onRowClick }) => {
  const extra = (rowClassName && rowClassName(row)) || '';
  const footer = layout.actions.map((c) => card_cell(c, row)).filter((v) => v);
  const value = (c) => {
    const v = card_cell(c, row);
    return v === null || v === undefined || v === '' ? null : v;
  };
  const cls = [
    'tp-rcard',
    'tp-rcard-track',
    layout.select ? 'tp-rcard-sel' : '',
    extra,
    onRowClick ? 'tp-row-click' : ''
  ]
    .filter(Boolean)
    .join(' ');
  return (
    // A mouse convenience only: the card's buttons are the keyboard path.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      data-row-key={rowKey(row)}
      className={cls}
      onClick={
        onRowClick
          ? (e) => {
              if (e.target.closest && e.target.closest(INTERACTIVE)) return;
              onRowClick(row);
            }
          : undefined
      }
    >
      {layout.select ? (
        // eslint-disable-next-line jsx-a11y/label-has-associated-control
        <label className="tp-rcard-check">{card_cell(layout.select, row)}</label>
      ) : null}
      <div className="tp-rcard-who">{layout.identifier ? value(layout.identifier) : null}</div>
      {layout.status ? (
        <div className="tp-rcard-st" data-role="status">
          <SrLabel c={layout.status} />
          {value(layout.status)}
        </div>
      ) : null}
      {layout.blocks.map((c) => {
        const v = value(c);
        return v === null ? null : (
          <div key={c.key} className="tp-rcard-blk" data-role={c.role || undefined}>
            <SrLabel c={c} />
            {v}
          </div>
        );
      })}
      {layout.notes.map((c) => {
        const v = value(c);
        return v === null ? null : (
          <div key={c.key} className="tp-rcard-blk tp-rcard-note">
            {v}
          </div>
        );
      })}
      {footer.length ? <div className="tp-rcard-ft">{footer}</div> : null}
    </div>
  );
};

// The sort select (cards, and a table whose sortable column has no header of its own).
const SortSelect = ({ options, sort, on_sort_set, sort_label, className = '' }) => {
  const id = useId();
  return (
    <div className={`tp-rcards-sort${className ? ` ${className}` : ''}`}>
      <SelectFilter
        id={id}
        label="Sort"
        aria_label={sort_label}
        options={options}
        value={card_sort_value(sort)}
        // no sort chosen yet: the list is in its default order, and the select says so
        all_label="Default order"
        active={false}
        onChange={(v) => {
          const next = parse_card_sort(v);
          if (next) on_sort_set(next);
        }}
      />
    </div>
  );
};

const CardsView = ({
  columns,
  rows,
  rowKey,
  sort,
  on_sort_set,
  sort_label,
  select_all,
  cardTitle,
  rowClassName,
  onRowClick
}) => {
  const tracked = is_tracked(columns);
  const layout = tracked ? track_card_layout(columns) : card_layout(columns, !!cardTitle);
  const sort_options = on_sort_set ? card_sort_options(columns) : [];
  return (
    <>
      {select_all || sort_options.length ? (
        <div className="tp-rcards-toolbar">
          {select_all ? (
            <CheckChip
              className="tp-rcards-all"
              checked={!!select_all.checked}
              onChange={(e) => select_all.on_change(e.target.checked)}
              disabled={!!select_all.disabled}
            >
              {select_all.label}
            </CheckChip>
          ) : null}
          {sort_options.length ? (
            <SortSelect
              options={sort_options}
              sort={sort}
              on_sort_set={on_sort_set}
              sort_label={sort_label}
            />
          ) : null}
        </div>
      ) : null}
      <div className="tp-rcards">
        {rows.map((row) =>
          tracked ? (
            <TrackCard
              key={rowKey(row)}
              layout={layout}
              row={row}
              rowKey={rowKey}
              rowClassName={rowClassName}
              onRowClick={onRowClick}
            />
          ) : (
            <Card
              key={rowKey(row)}
              layout={layout}
              row={row}
              rowKey={rowKey}
              cardTitle={cardTitle}
              rowClassName={rowClassName}
              onRowClick={onRowClick}
            />
          )
        )}
      </div>
    </>
  );
};

// Where a table-mode sort select goes: its .tp-card's header actions group, else the header.
export const header_sort_slot = (el) => {
  const card = el && el.closest ? el.closest('.tp-card') : null;
  const hd = card
    ? Array.from(card.children).find((n) => n.classList.contains('tp-card-hd'))
    : null;
  if (!hd) return null;
  return Array.from(hd.children).find((n) => n.classList.contains('tp-card-hd-acts')) || hd;
};

// Tests fail loudly when a column set would drop a money/status/date/count value in some layout.
const guard_values = (columns, has_title) => {
  if (import.meta.env.MODE !== 'test') return;
  [MODE_CARDS, MODE_TABLE, MODE_FOLD].forEach((layout) => {
    const dropped = dropped_values(columns, layout, 1, has_title);
    if (dropped.length) throw new Error(`ResponsiveTable ${layout} drops values: ${dropped}`);
  });
};

const ResponsiveTable = ({
  columns,
  rows,
  rowKey,
  emptyText,
  sort,
  on_sort,
  on_sort_set,
  sort_label = 'Sort',
  select_all,
  cardTitle,
  rowClassName,
  onRowClick,
  card_below
}) => {
  guard_values(columns, !!cardTitle);
  const tracked = is_tracked(columns);
  const { ref, mode, width, on_overflow } = useFitLayout({
    threshold: card_below || (tracked ? TRACK_CARD_BELOW : undefined),
    levels: fold_order(columns).length,
    content: rows
  });
  const [hd_slot, setHdSlot] = useState(null);
  useLayoutEffect(() => setHdSlot(header_sort_slot(ref.current)), [ref]);
  const header_sort =
    rows.length > 0 && mode !== MODE_CARDS && on_sort_set && has_extra_sorts(columns) ? (
      <SortSelect
        options={card_sort_options(columns)}
        sort={sort}
        on_sort_set={on_sort_set}
        sort_label={sort_label}
        className={hd_slot ? 'tp-rt-hd-sort' : ''}
      />
    ) : null;
  return (
    <div ref={ref} className="tp-tablewrap tp-rt" data-mode={mode_name(mode)}>
      {!rows.length ? (
        <div className="tp-empty">{emptyText || 'No records for the selected filters.'}</div>
      ) : null}
      {rows.length > 0 && mode === MODE_CARDS ? (
        <CardsView
          columns={columns}
          rows={rows}
          rowKey={rowKey}
          sort={sort}
          on_sort_set={on_sort_set}
          sort_label={sort_label}
          select_all={select_all}
          cardTitle={cardTitle}
          rowClassName={rowClassName}
          onRowClick={onRowClick}
        />
      ) : null}
      {/* Table mode: extra sorts go in the list header; outside a .tp-card they keep their own row. */}
      {header_sort && hd_slot ? createPortal(header_sort, hd_slot) : null}
      {header_sort && !hd_slot ? (
        <div className="tp-rcards-toolbar tp-rt-sortbar">{header_sort}</div>
      ) : null}
      {rows.length > 0 && mode !== MODE_CARDS ? (
        <TableView
          columns={columns}
          rows={rows}
          rowKey={rowKey}
          sort={sort}
          on_sort={on_sort}
          rowClassName={rowClassName}
          onRowClick={onRowClick}
          width={width}
          layout={mode}
          on_overflow={on_overflow}
        />
      ) : null}
    </div>
  );
};

export default ResponsiveTable;
